// Initial contact form state
export const getInitialContactFormState = () => ({
    name: '',
    email: '',
    phoneNumber: '',
    message: ''
});

// Checks if form is ready to submit (has all required fields)
export const isFormReadyToSubmit = (formData) => {
    return formData.name &&
        formData.name.trim().length > 0 &&
        formData.email &&
        formData.email.trim().length > 0 &&
        formData.message &&
        formData.message.trim().length > 0;
};

// Validates contact form fields
export const validateContactForm = (formData) => {
    const errors = {};
    let isValid = true;

    // Validate name
    if (!formData.name || formData.name.trim().length === 0) {
        errors.name = 'Name is required';
        isValid = false;
    } else if (formData.name.trim().length < 2) {
        errors.name = 'Name must be at least 2 characters long';
        isValid = false;
    } else if (formData.name.trim().length > 50) {
        errors.name = 'Name must be less than 50 characters';
        isValid = false;
    }

    // Validate email
    if (!formData.email || formData.email.trim().length === 0) {
        errors.email = 'Email is required';
        isValid = false;
    } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim())) {
            errors.email = 'Please enter a valid email address';
            isValid = false;
        }
    }

    // Validate phone number (optional but if provided, must be valid)
    if (formData.phoneNumber && formData.phoneNumber.trim().length > 0) {
        // Remove common formatting characters
        const cleanPhone = formData.phoneNumber.trim().replace(/[\s\-\(\)]/g, '');

        // Updated regex to accept both international and local formats
        // Local numbers can start with 0, international numbers start with + and country code
        const phoneRegex = /^(\+?[1-9][\d]{6,15}|0[1-9][\d]{6,14})$/;

        if (!phoneRegex.test(cleanPhone)) {
            errors.phoneNumber = 'Please enter a valid phone number';
            isValid = false;
        }
    }

    // Validate message
    if (!formData.message || formData.message.trim().length === 0) {
        errors.message = 'Message is required';
        isValid = false;
    } else if (formData.message.trim().length < 10) {
        errors.message = 'Message must be at least 10 characters long';
        isValid = false;
    } else if (formData.message.trim().length > 1000) {
        errors.message = 'Message must be less than 1000 characters';
        isValid = false;
    }

    return { isValid, errors };
};

// Sanitizes form input data
export const sanitizeFormData = (formData) => {
    const sanitized = {};

    Object.keys(formData).forEach(key => {
        if (typeof formData[key] === 'string') {
            // Remove leading/trailing whitespace and normalize spaces
            sanitized[key] = formData[key].trim().replace(/\s+/g, ' ');
        } else {
            sanitized[key] = formData[key];
        }
    });

    return sanitized;
};

// Handles form field changes
export const handleFormFieldChange = (event, setFormData, currentFormData) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
};

// Resets form to initial state
export const resetForm = (setFormData, initialState = null) => {
    const defaultState = initialState || getInitialContactFormState();
    setFormData(defaultState);
};

// Checks if form has unsaved changes
export const hasFormChanges = (currentFormData, initialFormData) => {
    const initial = initialFormData || getInitialContactFormState();

    return Object.keys(currentFormData).some(key =>
        currentFormData[key] !== initial[key]
    );
};

// Formats form data for submission
export const formatFormDataForSubmission = (formData) => {
    const formatted = { ...formData };

    // Ensure all required fields are present
    if (!formatted.name) formatted.name = '';
    if (!formatted.email) formatted.email = '';
    if (!formatted.phoneNumber) formatted.phoneNumber = '';
    if (!formatted.message) formatted.message = '';

    // Add timestamp
    formatted.timestamp = new Date().toISOString();

    // Add source information
    formatted.source = 'chatbot';

    return formatted;
};

// Validates email format
export const isValidEmail = (email) => {
    if (!email || typeof email !== 'string') return false;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
};

// Validates phone number format
export const isValidPhoneNumber = (phone) => {
    if (!phone || typeof phone !== 'string') return false;

    // Remove common formatting characters
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

    // Check if it's a valid phone number (7-15 digits, optionally starting with +)
    const phoneRegex = /^[\+]?[1-9][\d]{6,14}$/;
    return phoneRegex.test(cleanPhone);
};

// Gets form field error message
export const getFieldError = (errors, fieldName) => {
    return errors[fieldName] || '';
};

// Checks if form field has error
export const hasFieldError = (errors, fieldName) => {
    return !!errors[fieldName];
};

// Formats phone number for display
export const formatPhoneNumber = (phone) => {
    if (!phone) return '';

    // Remove all non-digit characters except +
    const clean = phone.replace(/[^\d+]/g, '');

    // Basic formatting for common patterns
    if (clean.startsWith('+94') && clean.length === 12) {
        // Sri Lanka format: +94 11 234 5678
        return `${clean.slice(0, 3)} ${clean.slice(3, 5)} ${clean.slice(5, 8)} ${clean.slice(8)}`;
    } else if (clean.startsWith('94') && clean.length === 11) {
        // Sri Lanka format without +: 94 11 234 5678
        return `${clean.slice(0, 2)} ${clean.slice(2, 4)} ${clean.slice(4, 7)} ${clean.slice(7)}`;
    } else if (clean.length === 10) {
        // Local format: 011 234 5678
        return `${clean.slice(0, 3)} ${clean.slice(3, 6)} ${clean.slice(6)}`;
    }

    return clean;
};

// Debounces form input to prevent excessive validation calls
export const debounceFormInput = (func, delay = 300) => {
    let timeoutId;

    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
};
